"use client";
// usePersonalizedSection — KATMAN 2'nin karar mantığı.
//
// Home iki katmanlı çalışıyor:
//   KATMAN 1 (sunucu): HomeSectionLoader section'ı ANONİM ve ISR cache'li çeker.
//                      Sayfa `○ (Static)` kalır, ilk boyama cache'ten anında
//                      gelir, SEO/LCP korunur.
//   KATMAN 2 (client): bu hook. Gerekli olduğunda aynı section'ı axios ile
//                      yeniden çeker — axios `Authorization` (giriş varsa) ve
//                      `X-Session-Id` header'larını gönderdiği için backend
//                      kullanıcıya özel içerik dönebilir.
//
// Neden kimlik sunucuya eklenmiyor: `cookies()` okumak home route'unu
// `ƒ (Dynamic)` yapar ve kimlikli bir yanıt tüm ziyaretçilerle paylaşılan
// cache'e yazılamaz.
//
// ── KATMAN 2 NE ZAMAN ÇALIŞIR ────────────────────────────────────────────────
// İki koşul da sağlanmalı; ikisi de gereksiz isteği kesmek için:
//
// 1) Auth token var mı?
//    Backend yalnızca `Authorization` ile kişiselleştiriyor. `X-Session-Id`
//    tek başına yanıtı değiştirmiyor — yani misafir için kimlikli istek,
//    ISR HTML'indeki veriyle birebir aynı yanıtı geri yazmaktan ibaret.
//    Misafirde hiç istek atmıyoruz; misafir ISR içeriğini anında ve skeleton
//    görmeden alıyor (SEO/crawler tarafı da bu yüzden bozulmuyor).
//
// 2) Section GERÇEKTEN kullanıcıya göre değişiyor mu? (slug allow-list)
//    Eskiden ölçüt `content_source !== "manual"` idi ve fazla genişti: canlı
//    API'de `automatic` işaretli 7 section var ama içerikleri kullanıcıya göre
//    değişmiyor — "İndirimli Ürünler", "Öne Çıkan Ürünler" vb. herkes için
//    aynı. Hepsi geçitle sarıldığı için girişli kullanıcı ana sayfanın yarısını
//    skeleton olarak görüyor, ISR'ın hazır içeriğini bekletiyordu.
//
//    Artık liste açık: yalnızca PERSONALIZED_SLUGS. Geri kalan her section —
//    automatic olsun olmasın — doğrudan ISR'dan basılır, istek atılmaz,
//    skeleton görünmez.
//
// ── SKELETON GEÇİDİ: KARAR REACT'TE DEĞİL, CSS'TE ────────────────────────────
// Kritik nokta: "anonim içerik mi, skeleton mı" kararı İLK BOYAMADA verilmiş
// olmalı. React'e bırakılamaz, çünkü hiçbir React kodu hydration'dan önce
// çalışmaz — HTML boyandığı an ile hydration'ın bittiği an arasında anonim
// ürünler ekranda kalır, sonra skeleton'a düşer. "Önce ürünler geldi, sonra
// skeleton çıktı" görüntüsü tam olarak bu pencere.
//
// Bu yüzden iş bölümü şöyle:
//   • <head>'deki bloklayıcı script (authGateScript) ilk boyamadan önce
//     <html>'e `has-auth` sınıfını ekler.
//   • globals.css `html.has-auth [data-hs="pending"]` altında anonim içeriği
//     gizleyip skeleton'ı gösterir. Misafirde sınıf yok → kurallar ölü.
//   • React yalnızca GEÇİDİ KAPATIR: `gateState` "ready" olunca CSS devreden
//     çıkar ve skeleton düğümü DOM'dan kalkar.
//
// `gateState` sunucuda ve client'ın İLK render'ında aynı değeri ("pending")
// üretmek zorunda — aksi hâlde hidrasyon uyuşmazlığı olur. `authPhase`
// başlangıçta "unknown" olduğu için bu garanti.
import { useEffect, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "@/app/lib/features/redux/redux.hook";
import { useIsAuthenticated } from "@/app/lib/features/auth/auth.hook";
import { tokenStorage } from "@/app/lib/api/tokenStorage";
import { getHomeSection } from "./home.thunk";
import { HomeAnySection } from "./home.state";
import { CollectionSection, Section } from "./home.types";

/**
 * İçeriği KULLANICIYA GÖRE DEĞİŞEN section'lar — slug bazlı, kapalı liste.
 *
 * Yalnızca bunlar kimlikli olarak yeniden çekilir ve yalnızca bunlar skeleton
 * geçidiyle sarılır. Listede olmayan her section (automatic işaretli olsa bile)
 * ISR'dan geldiği gibi basılır: istek yok, skeleton yok, bekleme yok.
 *
 * Ölçüt `content_source` DEĞİL: canlı API 7 section'ı `automatic` işaretliyor
 * ama bunların yalnızca "sana özel" olanı kişiye göre değişiyor; kalanı
 * ("İndirimli Ürünler", "Öne Çıkan Ürünler", "Avantajlı Ürünler" …) herkes için
 * aynı listeyi döndürüyor. Ayrıca `content_source` tip birleşiminde olmayan
 * değerler de gelebiliyor (`"slider"`, `"stories"`), yani o alan bu kararı
 * taşıyacak kadar güvenilir değil.
 *
 * Yeni bir kişisel section eklenirse slug'ı buraya yazılır — başka hiçbir yere
 * dokunmak gerekmez.
 */
const PERSONALIZED_SLUGS = new Set(["sana-ozel-urunler"]);

/**
 * Kimlikli tazelenen ama SKELETON GÖSTERMEYEN section'lar.
 *
 * Hikâyelerde kişiselleşen tek şey izlenme durumu (halka rengi); şeridin
 * kendisi herkeste aynı. Tüm şeridi skeleton'a çevirmek kazançtan çok kayıp
 * olurdu, o yüzden içerik ISR'dan anında basılıyor ve halka rengi arkada
 * sessizce güncelleniyor.
 */
const SILENT_REFRESH_SLUGS = new Set(["stories"]);

/**
 * Anonim sürüm ekrana BİR ŞEY basıyor mu.
 *
 * Geçidin tek işi "yanlış (anonim) içeriği girişli kullanıcıya göstermemek".
 * Anonim sürüm zaten boşsa saklanacak bir şey yok; buna rağmen geçit açılırsa
 * içeriği olmayan bir yere 380px'lik skeleton basılır ve istek dönünce hiçbir
 * şey bırakmadan kaybolur — girişli kullanıcıya hayalet skeleton.
 */
const hasRenderableContent = (section: HomeAnySection): boolean => {
  if (section.display_type === "collection") {
    return !!(section as CollectionSection).items?.length;
  }
  if (section.display_type === "tabs") {
    return !!(section as Section).items?.some((item) => item.products?.length);
  }
  return !!(section as Section).products?.length;
};

/**
 * Token'ın üç hâli. `useHasAuthToken` yerine burada yerel state tutulmasının
 * tek sebebi bu: o hook boolean döndürüyor ve "henüz bilmiyoruz" ile "misafir"
 * hâllerini ayırt edemiyor. Geçidi misafirde kapatabilmek (skeleton düğümünü
 * DOM'dan atmak) için bu ayrım şart. Token'ın effect'te okunma gerekçesi
 * aynı: cookie SSR'da yok, render sırasında okunursa hidrasyon uyuşmazlığı olur.
 */
type AuthPhase = "unknown" | "guest" | "user";

interface PersonalizedSectionResult {
  /** Render edilecek section — kimlikli sürüm varsa o, yoksa SSR'ın anonim sürümü. */
  section: HomeAnySection;
  /**
   * Bu section CSS geçidiyle sarılsın mı. Yalnızca ssrSection'dan türüyor,
   * yani mount sonrası DEĞİŞMEZ — sarmalayıcı div'in sonradan kaybolup
   * içeriği yeniden mount etmesini önlemek için bilerek böyle.
   */
  hasGate: boolean;
  /** Sarmalayıcının `data-hs` değeri. "pending" iken CSS girişli kullanıcıda skeleton'ı gösterir. */
  gateState: "pending" | "ready";
}

/**
 * @param slug       Section anahtarı — API'de key ve slug aynı değer.
 * @param ssrSection KATMAN 1'in anonim çıktısı.
 */
const usePersonalizedSection = (
  slug: string,
  ssrSection: HomeAnySection,
): PersonalizedSectionResult => {
  const dispatch = useAppDispatch();
  // Yalnızca "giriş var mı" gerekiyor; useAuth() altı selector kuruyor ve bu hook
  // ana sayfada her section için (19×) çalışıyor.
  const isAuthenticated = useIsAuthenticated();
  const [authPhase, setAuthPhase] = useState<AuthPhase>("unknown");
  const personalized = useAppSelector((state) => state.home.sections[slug]);
  const status = useAppSelector((state) => state.home.status[slug]);

  // isAuthenticated bağımlılığı: login/logout sonrası aynı sayfada kalındığında
  // faz yeniden hesaplansın.
  useEffect(() => {
    setAuthPhase(tokenStorage.getAccessToken() ? "user" : "guest");
  }, [isAuthenticated]);

  const isPersonalized = PERSONALIZED_SLUGS.has(slug);
  const needsRefetch = isPersonalized || SILENT_REFRESH_SLUGS.has(slug);

  useEffect(() => {
    if (authPhase !== "user" || !needsRefetch) return;
    dispatch(getHomeSection(slug));
  }, [dispatch, authPhase, needsRefetch, slug]);

  const hasGate = isPersonalized && hasRenderableContent(ssrSection);

  // Geçit kapanır: misafir olduğumuz kesinleşti, kimlikli sürüm geldi ya da
  // istek hata verdi. "error" hâli şart — yoksa başarısız istekte skeleton
  // kalıcı olurdu.
  const isSettled =
    authPhase === "guest" || !!personalized || status === "error";

  return {
    section: personalized ?? ssrSection,
    hasGate,
    gateState: isSettled ? "ready" : "pending",
  };
};

export default usePersonalizedSection;
